import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { getCompanyValues, createCompanyValue, updateCompanyValue, deleteCompanyValue } from '../services/companyValueService';

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const emptyForm = { name: '', color: COLORS[0], emoji: '', description: '' };

const Values = () => {
  const { currentUser } = useAppContext();
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = currentUser && ['admin', 'super_admin'].includes(currentUser.role);

  const fetchValues = async () => {
    try {
      const data = await getCompanyValues();
      setValues(data);
    } catch (err) {
      console.error('Error fetching values:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchValues(); }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startEdit = (v) => {
    setForm({ name: v.name, color: v.color || COLORS[0], emoji: v.emoji || '', description: v.description || '' });
    setEditingId(v.id);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingId) {
        await updateCompanyValue(editingId, form);
      } else {
        await createCompanyValue(form);
      }
      resetForm();
      await fetchValues();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save value');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this value?')) return;
    try {
      await deleteCompanyValue(id);
      await fetchValues();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete value');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-10 px-4">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold gradient-text">Company Values</h1>
          <p className="mt-2 text-gray-600">The principles that guide how we work together.</p>
        </div>
        {isAdmin && !showForm && (
          <button
            onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); setError(''); }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            + Add Value
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {/* Add / Edit form */}
      {showForm && isAdmin && (
        <div className="mb-8 bg-white rounded-xl shadow-md p-6 fade-in">
          <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Value' : 'New Value'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
                <input
                  type="text"
                  value={form.emoji}
                  onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                  placeholder="e.g. 🤝"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  maxLength={10}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A short description of this value"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <div className="flex gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Values grid */}
      {values.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-5xl mb-4">💡</div>
          <p className="text-lg">No company values defined yet.</p>
          {isAdmin && <p className="text-sm mt-1">Click "Add Value" to create your first one.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((v) => (
            <div
              key={v.id}
              className="bg-white rounded-xl shadow-md card-hover fade-in p-6 border-l-4 transition-all"
              style={{ borderLeftColor: v.color || '#3B82F6' }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-3xl mb-2">{v.emoji || '⭐'}</div>
                  <h3 className="text-xl font-bold text-gray-900">{v.name}</h3>
                  {v.description && (
                    <p className="mt-2 text-sm text-gray-600">{v.description}</p>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={() => startEdit(v)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(v.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Values;
